<template>
  <div :class="$attrs.class">
    <div v-if="label" class="tw-mb-1 tw-text-mb-body lg:tw-text-dt-body">
      <span :class="hasError ? 'tw-text-error' : 'tw-text-black'">
        {{ label }}
      </span>
      <span v-if="isRequired" class="tw-text-error">*</span>
    </div>
    <QInput
      v-model="innerValue"
      :data-cy="dataCy"
      :placeholder="placeholder"
      :rows="rows"
      :filled="filled"
      :outlined="outlined"
      :square="square"
      :dense="dense"
      :input-class="inputClass"
      :disable="disable"
      type="textarea"
      :counter="counter"
      :maxlength="maxlength"
      :rules="validationRules"
      :error="hasError"
      :error-message="errorMessage"
    >
      <slot />
    </QInput>
    <div
      :class="[
        'tw-flex tw-items-center',
        hasError ? 'tw-justify-between' : 'tw-justify-end'
      ]"
    >
      <div
        v-if="hasError"
        class="tw-body-tiny tw-text-error"
        :data-cy="`error-input-${dataCy}`"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { parsePhoneNumber } from 'awesome-phonenumber'

const props = withDefaults(defineProps<{
  modelValue?: string
  name?: string
  rules?: string
  label?: string
  required?: boolean
  dataCy?: string
  placeholder?: string
  rows?: string
  counter?: boolean
  maxlength?: string
  // Additional Quasar props
  filled?: boolean
  outlined?: boolean
  square?: boolean
  dense?: boolean
  inputClass?: string
  disable?: boolean
}>(), {
  modelValue: '',
  name: '',
  rules: '',
  label: '',
  required: false,
  dataCy: '',
  placeholder: '',
  rows: '10',
  counter: false,
  maxlength: '',
  // Additional Quasar props defaults
  filled: true,
  outlined: false,
  square: false,
  dense: false,
  inputClass: '',
  disable: false,
})

const innerValue = defineModel<string | number>({
  default: '',
})

const isRequired = computed(() => props.rules.includes('required'))
const hasError = ref(false)
const errorMessage = ref('')

// Convert vee-validate rules to Quasar rules
const validationRules = computed(() => {
  const rules = []

  if (props.rules.includes('required')) {
    rules.push((val: string) => !!val || 'This field is required')
  }

  if (props.rules.includes('email')) {
    rules.push((val: string) => {
      const emailPattern = /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/
      return !val || emailPattern.test(val) || 'Please enter a valid email address'
    })
  }

  if (props.rules.includes('phone')) {
    rules.push((val: string) => {
      if (!val) { return true }
      const regex = /\D/g
      const phoneNumber = val.replace(regex, '') || ''
      const pn = parsePhoneNumber(phoneNumber, { regionCode: 'TH' })
      return pn.valid || 'Please enter a valid phone number'
    })
  }

  return rules
})
</script>
